/* import __COLOCATED_TEMPLATE__ from './error.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import {
  findInSignalChain,
  tryParseFloat,
  addChartFn,
  getVoltRefErrors,
  getInvertingAmpErrors,
  getDacErrors,
  getOutAmpErrors,
  addDetailsForErrorCategory,
} from '../../utils/error-utils';
import Constants, { AMP_TYPES } from '../../utils/constants';

const PARAM = Constants.CDP.PARAMETERS;

export default class Error extends Component {
  @service intl;

  @action getErrorData() {
    const multiplyingDacTopologyInput = this.args.model.topologyInputHandler.current.find((input) =>
      input && input.config ? input.config.id === Constants.TOPOLOGY_INPUTS.MULTIPLYING_DAC : false,
    );
    let isTopologyInverting = false,
      isTopologyBuffered = false;

    if (multiplyingDacTopologyInput) {
      isTopologyInverting = multiplyingDacTopologyInput.isSelectedOptionInverting;
      isTopologyBuffered = multiplyingDacTopologyInput.isSelectedOptionBuffered;
    } else {
      console.error('Multiplying DAC topology input is unavailable.');
    }

    // let currentSignalChainParts = currentSignalChain.map(p => ((p.selected || {})[0] || {}).displayValue);
    const currentSignalChain = this.args.model.signalChainHandler.currentItems,
      // Objects and their respective error arrays
      dacSelected = this.args.model.signalChainHandler.dac.selected,
      voltRef = findInSignalChain(currentSignalChain, 'volt ref'),
      outAmp = findInSignalChain(currentSignalChain, 'output amp');

    let voltRefErrors = [];
    let outAmpErrors = [];

    // Inverting amp or buffer
    const invertingAmp = isTopologyBuffered
      ? findInSignalChain(currentSignalChain, 'buffer')
      : findInSignalChain(currentSignalChain, 'inv amp');
    const invertingAmpType = isTopologyBuffered ? AMP_TYPES.BUFFER : AMP_TYPES.INVERTING;

    let invertingAmpErrors = [];
    let invertingAmpOutputVoltage = null; // gets set by inverting amp checks

    const invertingResistor = this.args.model.resistorHandler.currentResistors.find((resistor) => resistor.isInverting);
    //#region Inverting amp checks
    // Inverting amplifier
    if (invertingAmp) {
      // If not external, Internal Reference Inverting Resistors (Ω) (R1 and R2) →
      // Used in the "Unipolar, Vout = 0 to +VREF" topology dor the Voltage Reference Inverting Amplifier.

      const { errors, outputVoltage } = getInvertingAmpErrors(
        invertingAmpType,
        invertingAmp,
        this.args.model.signalChainHandler.inputVoltage,
        dacSelected,
        this.args.model,
        invertingResistor,
      );
      invertingAmpErrors = errors;
      invertingAmpOutputVoltage = outputVoltage;
    }
    //#endregion

    // intermediate variables
    const dacOutputVoltage = isTopologyInverting // == "Unipolar, Vout = 0 to +VREF"
      ? invertingAmpOutputVoltage
      : tryParseFloat(voltRef, PARAM.VOUT); // Reference Voltage (V) (VREF)

    let dacSelectedErrors = getDacErrors(dacSelected, dacOutputVoltage, this.args.model);

    let dacNoiseErrors = [];
    const addNoiseDetails = addDetailsForErrorCategory(dacNoiseErrors, this.args.model);

    let vNoiseDensity = tryParseFloat(dacSelected, PARAM.NOISE_DENSITY); // 4101
    let multiplyingBW = tryParseFloat(dacSelected, PARAM.MULTIPLYING_BW); // 4182

    addNoiseDetails(
      'noise',
      [vNoiseDensity, multiplyingBW],
      ((vNoiseDensity * Math.sqrt(multiplyingBW) * 6.6) / dacOutputVoltage) * 1e6,
      this.intl.t('messages.error_params_not_available', {
        errorParams: [
          this.intl.t('labels.errors.vnoise_density'),
          this.intl.t('labels.errors.multiplying_bandwidth'),
        ].join(','),
      }),
      true, // show help topic
    );

    dacSelectedErrors = dacSelectedErrors.concat(dacNoiseErrors);

    // Zero Scale (todo: needs formula; does it exist?)

    // Voltage Reference
    if (voltRef) {
      voltRefErrors = getVoltRefErrors(voltRef, dacSelected, this.args.model);
    }

    // Output Amplifier associated errors
    if (outAmp) {
      // DAC Input Resistance (Ω) is R1 and Feedback Resistor (Ω) is R2 for the Output Inverting amplifier.
      // "Reference Input Resistance" doesn't appear to exist

      // let r1 = null;
      // let r2 = null;

      // let r = tryParseFloat(dacSelected, 6688) // Reference Internal Output Resistors

      // TODO: This will need to be passed in and should replace the calculation of output voltage
      // let fullScaleOutputVoltage = this.model.fullScaleOutputVoltage;
      const outputResistor = this.args.model.resistorHandler.currentResistors.find(
        (resistor) => resistor.id === Constants.RESISTORS.OUTPUT,
      );

      // TODO: This will need to be passed in as well and should replace the calculation of ideal gain
      // let outputGain = this.model.getOutputAmplifierGain();

      const outAmpErrorsObj = getOutAmpErrors(
        AMP_TYPES.INVERTING,
        outAmp,
        dacSelected,
        this.args.model,
        outputResistor,
        dacOutputVoltage,
      );
      outAmpErrors = outAmpErrorsObj.outAmpErrors;
    }

    let chartInfo = { charts: [] }; // Array of all charts
    let invertingAmpLabel = this.intl.t('labels.parts.inverting_amplifier');
    if (isTopologyBuffered) invertingAmpLabel = this.intl.t('labels.parts.buffer');
    const invertingAmpClass = isTopologyBuffered ? Constants.SVG_PARTS.buffer : Constants.SVG_PARTS.inverting_amplifier;
    // if(isTopologyNonInverting) invertingAmpLabel = (does not occur)

    let addChart = addChartFn(chartInfo.charts);
    if (isTopologyBuffered || isTopologyInverting) {
      addChart(invertingAmpLabel, invertingAmpClass, invertingAmpErrors);
    }
    addChart(this.intl.t('labels.parts.multiplying_dac'), Constants.SVG_PARTS.multiplying_dac, dacSelectedErrors);
    addChart(this.intl.t('labels.parts.voltage_reference'), Constants.SVG_PARTS.voltage_reference, voltRefErrors);
    addChart(this.intl.t('labels.parts.output_amplifier'), Constants.SVG_PARTS.output_amplifier, outAmpErrors);
    chartInfo.hasIdealVoltage = true;

    return chartInfo;
  }
}
